<template>
  <AppFooter class="platform-footer mt-3">
    <div class="platform-footer-content">
      <div class="platform-footer-meta">
        <span class="platform-footer-copyright">&copy; {{ currentYear }}</span>

        <a
          href="https://zyncro.ar"
          target="_blank"
          rel="noopener noreferrer"
          class="platform-footer-zyncro"
          :title="projectName || 'Zyncro'"
          :aria-label="`${projectName || 'Zyncro'} - sitio web de Zyncro`"
        >
          <img :src="zyncroIcon" alt="" aria-hidden="true" class="platform-footer-zyncro-icon" />
          <img :src="zyncroWordmark" alt="Zyncro" class="platform-footer-zyncro-wordmark" />
        </a>

        <span class="platform-footer-divider" aria-hidden="true"></span>

        <div v-if="developer.name" class="platform-footer-developer">
          <span class="platform-footer-label">Desarrollado por</span>
          <a
            :href="developer.url"
            target="_blank"
            rel="noopener noreferrer"
            class="platform-footer-brand"
          >
            <img
              v-if="isCarbiniDeveloper"
              :src="carbiniIcon"
              alt=""
              aria-hidden="true"
              class="platform-footer-brand-icon"
            />
            <span>{{ developer.name }}</span>
            <AppIcon icon="box-arrow-up-right" font-scale="0.7" aria-hidden="true"></AppIcon>
          </a>
        </div>
      </div>

      <router-link
        v-if="isStaff"
        :to="{ name: 'ChangelogStaff' }"
        class="platform-footer-changelog"
        title="Registro de cambios"
        aria-label="Abrir registro de cambios"
      >
        <AppIcon name="history" />
        <span class="platform-footer-changelog-label">Registro de cambios</span>
      </router-link>
    </div>
  </AppFooter>
</template>

<script>
import { Footer as AppFooter } from '@coreui/vue'
import zyncroIcon from '@/assets/branding/zyncro-icon.svg'
import zyncroWordmark from '@/assets/branding/zyncro-logo.svg'
import carbiniIcon from '@/assets/branding/carbini-icon.svg'

export default {
  name: 'DefaultFooter',
  components: {
    AppFooter,
  },
  props: {
    developer: {
      type: Object,
      default: () => ({ name: '', url: '' }),
    },
    projectName: {
      type: String,
      default: '',
    },
    isStaff: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      zyncroIcon,
      zyncroWordmark,
      carbiniIcon,
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    isCarbiniDeveloper() {
      return String(this.developer.name || '')
        .replace(/\s+/g, '')
        .toLowerCase() === 'carbini.dev'
    },
  },
}
</script>

<style scoped>
.platform-footer {
  min-height: 56px;
  padding: 0.65rem 1rem;
  border-top: 1px solid var(--app-color-border);
  background: var(--app-color-surface);
  color: var(--app-color-text-muted);
  box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.035);
}

.platform-footer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
}

.platform-footer-meta,
.platform-footer-developer,
.platform-footer-zyncro,
.platform-footer-brand,
.platform-footer-changelog {
  display: inline-flex;
  align-items: center;
}

.platform-footer-meta {
  flex-wrap: wrap;
  gap: 0.65rem;
  min-width: 0;
}

.platform-footer-copyright {
  color: var(--app-color-text-muted);
  font-size: 0.75rem;
  white-space: nowrap;
}

.platform-footer-zyncro {
  gap: 0.3rem;
  color: inherit;
  text-decoration: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.platform-footer-zyncro:hover,
.platform-footer-zyncro:focus {
  opacity: 0.78;
  outline: none;
  text-decoration: none;
  transform: translateY(-1px);
}

.platform-footer-zyncro-icon {
  display: block;
  width: 20px;
  height: 20px;
}

.platform-footer-zyncro-wordmark {
  display: block;
  width: 50px;
  height: auto;
  transform: translateY(1px);
}

.platform-footer-divider {
  width: 1px;
  height: 18px;
  background: var(--app-color-border);
}

.platform-footer-developer {
  gap: 0.45rem;
}

.platform-footer-label {
  color: var(--app-color-text-subtle);
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.platform-footer-brand {
  gap: 0.4rem;
  padding: 0.3rem 0.55rem;
  border: 1px solid var(--app-color-border);
  border-radius: 8px;
  background: var(--app-color-surface-subtle);
  color: var(--app-color-text) !important;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  text-decoration: none !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.platform-footer-brand:hover,
.platform-footer-brand:focus {
  border-color: var(--app-color-primary-200);
  color: var(--app-color-primary-active) !important;
  box-shadow: var(--app-shadow-sm);
  outline: none;
  transform: translateY(-1px);
}

.platform-footer-brand-icon {
  display: block;
  width: 12px;
  height: 18px;
  object-fit: contain;
}

.platform-footer-changelog {
  flex-shrink: 0;
  gap: 0.45rem;
  padding: 0.4rem 0.65rem;
  border: 1px solid transparent;
  border-radius: 8px;
  color: var(--app-color-text-muted) !important;
  font-size: 0.72rem;
  font-weight: 600;
  text-decoration: none !important;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.platform-footer-changelog:hover,
.platform-footer-changelog:focus {
  border-color: var(--app-color-primary-200);
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary) !important;
  outline: none;
}

@media (max-width: 575.98px) {
  .platform-footer {
    min-height: 48px;
    padding: 0.5rem 0.65rem;
  }

  .platform-footer-content {
    flex-direction: row;
    gap: 0.35rem;
    white-space: nowrap;
  }

  .platform-footer-meta {
    flex-wrap: nowrap;
    gap: 0.35rem;
  }

  .platform-footer-label,
  .platform-footer-brand .b-icon,
  .platform-footer-changelog-label {
    display: none;
  }

  .platform-footer-developer {
    gap: 0;
  }

  .platform-footer-copyright,
  .platform-footer-brand {
    font-size: 0.65rem;
  }

  .platform-footer-zyncro-icon {
    width: 17px;
    height: 17px;
  }

  .platform-footer-zyncro-wordmark {
    width: 42px;
  }

  .platform-footer-brand {
    padding: 0.25rem 0.4rem;
  }

  .platform-footer-brand-icon {
    width: 10px;
    height: 15px;
  }

  .platform-footer-changelog {
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
  }
}
</style>
